import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Snackbar } from '@mui/material';
import VideoPlayer from '../../components/VideoPlayer'; // Adjust the import path as necessary
import { approveVideo_AWS, getVideoURL, rejectVideo_AWS, updateRMAStatus } from '../../apis/capsuleAPI';
import { addLog } from '../../apis/logAPI';

const VideoPlayerPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sn = searchParams.get('sn');
  const [videoMeta, setVideoMeta] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRejectNoteModal, setShowRejectNoteModal] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [notification, setNotification] = useState({
    open: false,
    severity: 'success',
    message: ''
  });

  useEffect(() => {
    const fetchVideo = async () => {
      try {
        const { capsule, videoPackage } = await getVideoURL(sn);

        console.log("Loading video data for SN:", capsule.SN);
        console.log("Video package:", videoPackage);

        const response = await fetch(videoPackage.vidMetaUrl);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // 3. Convert the response stream directly to a JS Object
        const jsonData = await response.json();

        setVideoMeta({
          SN: capsule.SN,
          video_status: capsule.video_status,
          duoVidSrc: videoPackage.duoVidSrc,
          panoVidSrc: videoPackage.panoVidSrc,
          vidMeta: jsonData
        })
      } catch (error) {
        console.error("Error getting video data:", error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchVideo();
  }, [sn]);

  const showNotification = (severity, message) => {
    setNotification({ open: true, severity, message });
  };

  const exitVideoPage = (delayMs = 100) => {
    setTimeout(() => {
      window.close();
      setTimeout(() => {
        navigate(-1);
      }, 100);
    }, delayMs);
  };

  const handleApprove = async () => {
    if (!videoMeta?.SN || isSubmitting) return;

    try {
      setIsSubmitting(true);
      await approveVideo_AWS({ SN: videoMeta.SN });
      await addLog({ SN: videoMeta.SN, log_type: 'General Log', log_info: 'Reviewed and approved' });
      showNotification('success', `Capsule ${videoMeta.SN} approved successfully.`);
      exitVideoPage(1200);
    } catch (actionError) {
      console.error('Error approving video:', actionError);
      showNotification('error', `Failed to approve video: ${actionError.message || 'Unknown error'}`);
      setIsSubmitting(false);
    }
  };

  const handleReject = () => {
    if (!videoMeta?.SN || isSubmitting) return;
    setRejectNote('');
    setShowRejectNoteModal(true);
  };

  const handleRejectWithNoteSubmit = async () => {
    if (!videoMeta?.SN || isSubmitting) return;

    try {
      setIsSubmitting(true);
      await updateRMAStatus(videoMeta.SN, true, rejectNote);
      await rejectVideo_AWS({ SN: videoMeta.SN });

      try {
        await addLog({
          SN: videoMeta.SN,
          log_type: 'General Log',
          log_info: 'Reviewed and rejected',
          log_date: new Date().toISOString()
        });
        await addLog({
          SN: videoMeta.SN,
          log_type: 'Video Issue Log',
          log_info: 'Video is rejected by download center operator',
          log_date: new Date().toISOString()
        });
      } catch (logError) {
        console.error('Error updating logs after rejection:', logError);
      }

      setShowRejectNoteModal(false);
      showNotification('success', `Capsule ${videoMeta.SN} rejected successfully.`);
      exitVideoPage(1200);
    } catch (actionError) {
      console.error('Error rejecting video:', actionError);
      showNotification('error', `Failed to reject video: ${actionError.message || 'Unknown error'}`);
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!videoMeta) {
    return <div>No video data available</div>;
  }

  return (
    <div className="VideoPlayerPage">
      <Snackbar
        open={notification.open}
        autoHideDuration={2500}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      {showRejectNoteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              Add Investigation Note for Capsule {videoMeta.SN}
            </h2>
            <textarea
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              className="w-full min-h-[140px] rounded-md border border-gray-300 p-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="Enter investigation note (RMA note) before rejecting..."
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  if (isSubmitting) return;
                  setShowRejectNoteModal(false);
                }}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRejectWithNoteSubmit}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit & Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
      <VideoPlayer
        videoData={{
          duoVidSrc: videoMeta.duoVidSrc,
          panoVidSrc: videoMeta.panoVidSrc,
          vidMeta: videoMeta.vidMeta,
          SN: videoMeta.SN,
          video_status: videoMeta.video_status
        }}
        initialMode="P"
        onApproveVideo={handleApprove}
        onRejectVideo={handleReject}
        isActionSubmitting={isSubmitting}
      />
    </div>
  );
};

export default VideoPlayerPage;
